import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CreditCardProviderLogo } from "@/components/CreditCardProviderLogo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CreditCard,
  Plus,
  RefreshCw,
  Trash2,
  Shield,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Info,
  Wifi,
  Search,
  Sparkles,
  Pencil,
  Eye,
  Download,
  AlertTriangle,
  ArrowRight,
  Replace,
  Copy,
  Calendar,
  SkipForward,
  Upload,
  X,
} from "lucide-react";
import {
  creditCardSyncApi,
  categoryApi,
  recurringPaymentApi,
  type CreditCardConnectionDto,
  type CreditCardProvider,
  type ApiCategory,
  type ApiRecurringPayment,
  type DiscoveredCard,
  type PotentialDuplicate,
  type DuplicateResolution,
  type CreditCardSyncResult,
  type RecurringOverlap,
} from "@/lib/api";
import { format } from "date-fns";

const PROVIDERS: { value: CreditCardProvider; label: string }[] = [
  { value: "visaCal", label: "creditCardSync.providers.visaCal" },
  { value: "max", label: "creditCardSync.providers.max" },
  { value: "isracard", label: "creditCardSync.providers.isracard" },
];

type AddStep = "form" | "connecting" | "selectCards" | "initialImport" | "archiveRecurring";

function PulsingDots() {
  return (
    <span className="inline-flex items-center gap-1 ml-1">
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-[pulse-dot_1.4s_ease-in-out_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-[pulse-dot_1.4s_ease-in-out_0.2s_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-[pulse-dot_1.4s_ease-in-out_0.4s_infinite]" />
    </span>
  );
}

function ConnectingAnimation({ provider, step }: { provider: string; step: number }) {
  const { t } = useTranslation();

  const steps = [
    { icon: Wifi, text: t("creditCardSync.connectingTo", { provider }), active: step >= 0 },
    { icon: Search, text: t("creditCardSync.searchingCards"), active: step >= 1 },
    { icon: Sparkles, text: t("creditCardSync.almostThere"), active: step >= 2 },
  ];

  return (
    <div className="flex flex-col items-center justify-center py-8 space-y-8">
      {/* Animated card icon */}
      <div className="relative">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center animate-[float_3s_ease-in-out_infinite]">
          <CreditCard className="h-10 w-10 text-primary animate-[shimmer_2s_ease-in-out_infinite]" />
        </div>
        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary/20 animate-ping" />
        <div className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-primary/10 animate-ping [animation-delay:0.5s]" />
      </div>

      {/* Step indicators */}
      <div className="space-y-3 w-full max-w-xs">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-500 ${
                s.active
                  ? "bg-primary/5 text-primary"
                  : "text-muted-foreground/40"
              }`}
              style={{
                opacity: s.active ? 1 : 0.3,
                transform: s.active ? "translateX(0)" : "translateX(-8px)",
                transition: `all 0.5s ease ${i * 0.15}s`,
              }}
            >
              {s.active && step === i ? (
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              ) : s.active && step > i ? (
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              ) : (
                <Icon className="h-4 w-4 shrink-0" />
              )}
              <span className="text-sm font-medium">
                {s.text}
                {s.active && step === i && <PulsingDots />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CardSelector({
  cards,
  selectedCards,
  onToggle,
  onSelectAll,
}: {
  cards: DiscoveredCard[];
  selectedCards: Set<string>;
  onToggle: (accountNumber: string) => void;
  onSelectAll: () => void;
}) {
  const { t } = useTranslation();
  const allSelected = cards.length > 0 && cards.every((c) => selectedCards.has(c.accountNumber));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <span className="text-sm font-medium text-green-600">
            {t("creditCardSync.cardsFound", { count: cards.length })}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onSelectAll}>
          {allSelected ? t("creditCardSync.selectAll") : t("creditCardSync.selectAll")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">{t("creditCardSync.selectCardsDesc")}</p>

      <div className="space-y-2">
        {cards.map((card, i) => {
          const last4 = card.accountNumber.slice(-4) || "????";
          const isSelected = selectedCards.has(card.accountNumber);

          return (
            <div
              key={card.accountNumber}
              onClick={() => onToggle(card.accountNumber)}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                isSelected
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/30 hover:bg-muted/50"
              }`}
              style={{
                animationDelay: `${i * 100}ms`,
                animation: "slideUp 0.4s ease-out forwards",
                opacity: 0,
              }}
            >
              <Checkbox checked={isSelected} className="pointer-events-none" />
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-800 to-slate-600 flex items-center justify-center shadow-inner">
                <CreditCard className="h-5 w-5 text-white/90" />
              </div>
              <div className="flex-1">
                <p className="font-mono font-semibold text-sm tracking-wider">
                  {t("creditCardSync.cardNumber", { last4 })}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("creditCardSync.cardTransactions", { count: card.transactionCount })}
                </p>
              </div>
              {isSelected && (
                <CheckCircle2 className="h-5 w-5 text-primary animate-[scaleIn_0.2s_ease-out]" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SyncProgressAnimation({ provider, step }: { provider: string; step: number }) {
  const { t } = useTranslation();

  const steps = [
    { icon: Wifi, text: t("creditCardSync.syncStepConnect", { provider }), active: step >= 0 },
    { icon: Shield, text: t("creditCardSync.syncStepLogin"), active: step >= 1 },
    { icon: Download, text: t("creditCardSync.syncStepFetch"), active: step >= 2 },
    { icon: Sparkles, text: t("creditCardSync.syncStepProcess"), active: step >= 3 },
  ];

  const done = step >= 4;

  return (
    <div className="flex flex-col items-center justify-center py-8 space-y-8">
      <div className="relative">
        <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${
          done
            ? "bg-green-100 dark:bg-green-900/30"
            : "bg-gradient-to-br from-primary/20 to-primary/5 animate-[float_3s_ease-in-out_infinite]"
        }`}>
          {done ? (
            <CheckCircle2 className="h-10 w-10 text-green-500 animate-[scaleIn_0.3s_ease-out]" />
          ) : (
            <RefreshCw className="h-10 w-10 text-primary animate-spin" style={{ animationDuration: "2s" }} />
          )}
        </div>
        {!done && (
          <>
            <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary/20 animate-ping" />
            <div className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-primary/10 animate-ping [animation-delay:0.5s]" />
          </>
        )}
      </div>

      <div className="space-y-3 w-full max-w-xs">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const isCurrent = s.active && step === i;
          const isComplete = s.active && step > i;
          return (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-500 ${
                s.active
                  ? "bg-primary/5 text-primary"
                  : "text-muted-foreground/40"
              }`}
              style={{
                opacity: s.active ? 1 : 0.3,
                transform: s.active ? "translateX(0)" : "translateX(-8px)",
                transition: `all 0.5s ease ${i * 0.15}s`,
              }}
            >
              {isCurrent ? (
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              ) : isComplete || done ? (
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              ) : (
                <Icon className="h-4 w-4 shrink-0" />
              )}
              <span className="text-sm font-medium">
                {s.text}
                {isCurrent && !done && <PulsingDots />}
              </span>
            </div>
          );
        })}
      </div>

      {done && (
        <p className="text-sm text-green-600 font-medium animate-[slideUp_0.3s_ease-out]">
          {t("creditCardSync.syncComplete")}
        </p>
      )}
    </div>
  );
}

export default function CreditCardSync() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "he";

  const [connections, setConnections] = useState<CreditCardConnectionDto[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncStep, setSyncStep] = useState<Record<string, number>>({});

  type SyncBanner = {
    type: "success" | "error";
    imported: number;
    skipped: number;
    duplicates: number;
    error?: string;
  };
  const [syncBanners, setSyncBanners] = useState<Record<string, SyncBanner>>({});

  // Add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addStep, setAddStep] = useState<AddStep>("form");
  const [connectAnimStep, setConnectAnimStep] = useState(0);
  const [provider, setProvider] = useState<CreditCardProvider>("visaCal");
  const [displayName, setDisplayName] = useState("");
  const [defaultCategoryId, setDefaultCategoryId] = useState<string>("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [israeliId, setIsraeliId] = useState("");
  const [card6Digits, setCard6Digits] = useState("");

  // Cards discovered
  const [discoveredCards, setDiscoveredCards] = useState<DiscoveredCard[]>([]);
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const pendingCredentials = useRef<Record<string, string> | null>(null);
  const pendingImportDaysBack = useRef<number | undefined>(undefined);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editConnection, setEditConnection] = useState<CreditCardConnectionDto | null>(null);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editDefaultCategoryId, setEditDefaultCategoryId] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editIsraeliId, setEditIsraeliId] = useState("");
  const [editCard6Digits, setEditCard6Digits] = useState("");
  const [editStep, setEditStep] = useState<"form" | "connecting" | "selectCards">("form");
  const [editAnimStep, setEditAnimStep] = useState(0);
  const [editDiscoveredCards, setEditDiscoveredCards] = useState<DiscoveredCard[]>([]);
  const [editSelectedCards, setEditSelectedCards] = useState<Set<string>>(new Set());
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [showEditCredentials, setShowEditCredentials] = useState(false);

  // (sync progress now shown via toast notifications)

  // Duplicate review dialog
  const [duplicateReviewOpen, setDuplicateReviewOpen] = useState(false);
  const [pendingDuplicates, setPendingDuplicates] = useState<(PotentialDuplicate & { connectionId: string })[]>([]);
  const [duplicateActions, setDuplicateActions] = useState<Record<string, 'replace' | 'keep_both'>>({});
  const [isResolvingDuplicates, setIsResolvingDuplicates] = useState(false);

  // Recurring overlap dialog
  const [recurringOverlapOpen, setRecurringOverlapOpen] = useState(false);
  const [pendingOverlaps, setPendingOverlaps] = useState<RecurringOverlap[]>([]);
  const [selectedOverlaps, setSelectedOverlaps] = useState<Set<string>>(new Set());
  const [isDeactivatingRecurring, setIsDeactivatingRecurring] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<string | null>(null);

  // Recurring payments for the add-flow archive step
  const [addFlowRecurring, setAddFlowRecurring] = useState<ApiRecurringPayment[]>([]);
  const [addFlowSelectedRecurring, setAddFlowSelectedRecurring] = useState<Set<string>>(new Set());

  // "No history" prompt — user decides whether to import last 30 days
  const [noHistoryDialogOpen, setNoHistoryDialogOpen] = useState(false);
  const [noHistoryConnectionId, setNoHistoryConnectionId] = useState<string | null>(null);
  const [noHistoryProvider, setNoHistoryProvider] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [connectionsData, categoriesData] = await Promise.all([
        creditCardSyncApi.getConnections(),
        categoryApi.getAll(),
      ]);
      setConnections(connectionsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load credit card connections");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setProvider("visaCal");
    setDisplayName("");
    setDefaultCategoryId("");
    setUsername("");
    setPassword("");
    setIsraeliId("");
    setCard6Digits("");
    setAddStep("form");
    setConnectAnimStep(0);
    setDiscoveredCards([]);
    setSelectedCards(new Set());
    pendingCredentials.current = null;
    pendingImportDaysBack.current = undefined;
  };

  const buildCredentials = (): Record<string, string> | null => {
    const credentials: Record<string, string> = {};
    if (provider === "isracard") {
      if (!israeliId || !card6Digits || !password) {
        toast.error("All credential fields are required");
        return null;
      }
      credentials.id = israeliId;
      credentials.card6Digits = card6Digits;
      credentials.password = password;
    } else {
      if (!username || !password) {
        toast.error("Username and password are required");
        return null;
      }
      credentials.username = username;
      credentials.password = password;
    }
    return credentials;
  };

  const handleTestConnection = async () => {
    if (!displayName.trim()) {
      toast.error(t("creditCardSync.displayName") + " is required");
      return;
    }

    const credentials = buildCredentials();
    if (!credentials) return;
    pendingCredentials.current = credentials;

    setAddStep("connecting");
    setConnectAnimStep(0);

    const stepTimer1 = setTimeout(() => setConnectAnimStep(1), 1500);
    const stepTimer2 = setTimeout(() => setConnectAnimStep(2), 3500);

    try {
      const testResult = await creditCardSyncApi.testConnection({ provider, credentials });

      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);

      if (!testResult.success) {
        toast.error(testResult.error || t("creditCardSync.verifyFailed"));
        setAddStep("form");
        return;
      }

      if (testResult.cards && testResult.cards.length > 0) {
        setDiscoveredCards(testResult.cards);
        setSelectedCards(new Set(testResult.cards.map((c) => c.accountNumber)));
        setAddStep("selectCards");
      } else {
        setAddStep("initialImport");
      }
    } catch (error) {
      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
      console.error("Test connection failed:", error);
      toast.error(error instanceof Error ? error.message : "Connection test failed");
      setAddStep("form");
    }
  };

  const handleConfirmCards = async () => {
    if (selectedCards.size === 0) {
      toast.error(t("creditCardSync.noCardsSelected"));
      return;
    }
    if (!pendingCredentials.current) return;

    setAddStep("initialImport");
  };

  const proceedAfterImportChoice = async (daysBack?: number) => {
    if (!pendingCredentials.current) return;
    pendingImportDaysBack.current = daysBack;

    try {
      const active = await recurringPaymentApi.getActive();
      if (active.length > 0) {
        setAddFlowRecurring(active);
        setAddFlowSelectedRecurring(new Set(active.map((rp) => rp.id)));
        setAddStep("archiveRecurring");
        return;
      }
    } catch {}

    await saveConnectionAndSync(
      pendingCredentials.current,
      Array.from(selectedCards),
      undefined,
      daysBack,
    );
  };

  const saveConnectionAndSync = async (
    credentials: Record<string, string>,
    cards: string[],
    recurringToArchive?: string[],
    daysBack?: number,
  ) => {
    try {
      const connection = await creditCardSyncApi.createConnection({
        provider,
        displayName: displayName.trim(),
        credentials,
        defaultCategoryId: defaultCategoryId || undefined,
        selectedCards: cards.length > 0 ? cards : undefined,
      });

      setConnections((prev) => [connection, ...prev]);
      toast.success(t("creditCardSync.connectionAdded"));
      setAddDialogOpen(false);
      resetForm();

      if (recurringToArchive && recurringToArchive.length > 0) {
        try {
          const archiveResult = await creditCardSyncApi.deactivateRecurring(recurringToArchive);
          toast.success(`${archiveResult.deactivated} ${t("creditCardSync.recurringArchived")}`);
        } catch {
          toast.error(t("creditCardSync.archiveFailed"));
        }
      }

      if (daysBack === undefined) {
        await loadData();
        return;
      }

      const toastId = toast.loading(t("creditCardSync.importingLastMonth"), { duration: Infinity });
      try {
        const results = await creditCardSyncApi.sync(connection.id, daysBack);
        const result = results[0];
        if (result?.success) {
          const allDuplicates = collectDuplicates(results, connection.id);
          if (allDuplicates.length > 0) {
            setPendingDuplicates(allDuplicates);
            const defaultActions: Record<string, 'replace' | 'keep_both'> = {};
            allDuplicates.forEach((d) => {
              defaultActions[d.existingTransactionId] = 'replace';
            });
            setDuplicateActions(defaultActions);
            setDuplicateReviewOpen(true);
          }
          toast.success(
            `${t("creditCardSync.syncSuccess")}: ${result.transactionsImported} ${t("creditCardSync.imported")}, ${result.transactionsSkipped} ${t("creditCardSync.skipped")}`,
            { id: toastId },
          );
        } else {
          toast.error(
            `${t("creditCardSync.syncFailed")}: ${result?.error || "Unknown error"}`,
            { id: toastId },
          );
        }
        await loadData();
      } catch {
        toast.error(t("creditCardSync.syncFailed"), { id: toastId });
      }
    } catch (error) {
      console.error("Failed to save connection:", error);
      toast.error(error instanceof Error ? error.message : "Failed to add connection");
    }
  };

  const handleAddFlowArchiveConfirm = async () => {
    if (!pendingCredentials.current) return;
    await saveConnectionAndSync(
      pendingCredentials.current,
      Array.from(selectedCards),
      Array.from(addFlowSelectedRecurring),
      pendingImportDaysBack.current,
    );
  };

  const handleAddFlowArchiveSkip = async () => {
    if (!pendingCredentials.current) return;
    await saveConnectionAndSync(
      pendingCredentials.current,
      Array.from(selectedCards),
      undefined,
      pendingImportDaysBack.current,
    );
  };

  

  const openEditDialog = (connection: CreditCardConnectionDto) => {
    setEditConnection(connection);
    setEditDisplayName(connection.displayName);
    setEditDefaultCategoryId(connection.defaultCategoryId || "");
    setEditUsername("");
    setEditPassword("");
    setEditIsraeliId("");
    setEditCard6Digits("");
    setEditStep("form");
    setEditAnimStep(0);
    setEditDiscoveredCards([]);
    setEditSelectedCards(
      new Set(connection.selectedCards || []),
    );
    setShowEditCredentials(false);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editConnection) return;
    try {
      setIsSavingEdit(true);
      const update: Record<string, any> = {
        displayName: editDisplayName.trim(),
        defaultCategoryId: editDefaultCategoryId || undefined,
        selectedCards: editSelectedCards.size > 0 ? Array.from(editSelectedCards) : undefined,
      };
      const creds = buildEditCredentials();
      if (creds) update.credentials = creds;

      const updated = await creditCardSyncApi.updateConnection(editConnection.id, update);
      setConnections((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      toast.success(t("creditCardSync.connectionUpdated"));
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Failed to update:", error);
      toast.error(error instanceof Error ? error.message : "Failed to update connection");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const buildEditCredentials = (): Record<string, string> | null => {
    if (!editConnection) return null;
    if (editConnection.provider === "isracard") {
      if (!editIsraeliId && !editCard6Digits && !editPassword) return null;
      if (!editIsraeliId || !editCard6Digits || !editPassword) {
        toast.error("All credential fields are required when updating credentials");
        return null;
      }
      return { id: editIsraeliId, card6Digits: editCard6Digits, password: editPassword };
    } else {
      if (!editUsername && !editPassword) return null;
      if (!editUsername || !editPassword) {
        toast.error("Both username and password are required when updating credentials");
        return null;
      }
      return { username: editUsername, password: editPassword };
    }
  };

  const handleEditTestConnection = async () => {
    if (!editConnection) return;
    const creds = buildEditCredentials();
    if (!creds) {
      toast.error(t("creditCardSync.editCredentialsRequired"));
      return;
    }

    setEditStep("connecting");
    setEditAnimStep(0);

    const stepTimer1 = setTimeout(() => setEditAnimStep(1), 1500);
    const stepTimer2 = setTimeout(() => setEditAnimStep(2), 3500);

    try {
      const testResult = await creditCardSyncApi.testConnection({
        provider: editConnection.provider,
        credentials: creds,
      });

      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);

      if (!testResult.success) {
        toast.error(testResult.error || t("creditCardSync.verifyFailed"));
        setEditStep("form");
        return;
      }

      if (testResult.cards && testResult.cards.length > 0) {
        setEditDiscoveredCards(testResult.cards);
        setEditSelectedCards(new Set(testResult.cards.map((c) => c.accountNumber)));
        setEditStep("selectCards");
      } else {
        toast.success(t("creditCardSync.connectionVerified"));
        setEditStep("form");
      }
    } catch (error) {
      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
      console.error("Edit test connection failed:", error);
      toast.error(error instanceof Error ? error.message : "Connection test failed");
      setEditStep("form");
    }
  };

  const editToggleCard = (accountNumber: string) => {
    setEditSelectedCards((prev) => {
      const next = new Set(prev);
      if (next.has(accountNumber)) next.delete(accountNumber);
      else next.add(accountNumber);
      return next;
    });
  };

  const editToggleSelectAll = () => {
    if (editDiscoveredCards.every((c) => editSelectedCards.has(c.accountNumber))) {
      setEditSelectedCards(new Set());
    } else {
      setEditSelectedCards(new Set(editDiscoveredCards.map((c) => c.accountNumber)));
    }
  };

  const handleDelete = async () => {
    if (!connectionToDelete) return;
    try {
      await creditCardSyncApi.deleteConnection(connectionToDelete);
      setConnections((prev) => prev.filter((c) => c.id !== connectionToDelete));
      toast.success(t("creditCardSync.deleteSuccess"));
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Failed to delete connection");
    } finally {
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      const updated = await creditCardSyncApi.updateConnection(id, { enabled });
      setConnections((prev) => prev.map((c) => (c.id === id ? updated : c)));
      toast.success(t("creditCardSync.connectionUpdated"));
    } catch (error) {
      console.error("Failed to update:", error);
      toast.error("Failed to update connection");
    }
  };

  const handleSync = async (connectionId: string, daysBack?: number) => {
    const conn = connections.find((c) => c.id === connectionId);
    const providerName = conn ? providerLabel(conn.provider) : "";

    setIsSyncing(connectionId);
    setSyncBanners((prev) => { const next = { ...prev }; delete next[connectionId]; return next; });
    setSyncStep((prev) => ({ ...prev, [connectionId]: 0 }));

    const stepTimers = [
      setTimeout(() => setSyncStep((prev) => ({ ...prev, [connectionId]: 1 })), 2000),
      setTimeout(() => setSyncStep((prev) => ({ ...prev, [connectionId]: 2 })), 6000),
      setTimeout(() => setSyncStep((prev) => ({ ...prev, [connectionId]: 3 })), 12000),
    ];

    try {
      const results = await creditCardSyncApi.sync(connectionId, daysBack);
      stepTimers.forEach(clearTimeout);
      setSyncStep((prev) => { const next = { ...prev }; delete next[connectionId]; return next; });

      const result = results[0];
      if (result?.noHistory) {
        setNoHistoryConnectionId(connectionId);
        setNoHistoryProvider(providerName);
        setNoHistoryDialogOpen(true);
        return;
      }

      if (result?.success) {
        const allDuplicates = collectDuplicates(results, connectionId);
        if (allDuplicates.length > 0) {
          setPendingDuplicates(allDuplicates);
          const defaultActions: Record<string, 'replace' | 'keep_both'> = {};
          allDuplicates.forEach((d) => {
            defaultActions[d.existingTransactionId] = 'replace';
          });
          setDuplicateActions(defaultActions);
          setDuplicateReviewOpen(true);
        }

        setSyncBanners((prev) => ({
          ...prev,
          [connectionId]: {
            type: "success",
            imported: result.transactionsImported,
            skipped: result.transactionsSkipped,
            duplicates: allDuplicates.length,
          },
        }));
        showOverlapsIfAny(results);
      } else {
        setSyncBanners((prev) => ({
          ...prev,
          [connectionId]: {
            type: "error",
            imported: 0,
            skipped: 0,
            duplicates: 0,
            error: result?.error || "Unknown error",
          },
        }));
      }

      await loadData();
    } catch (error) {
      console.error("Sync failed:", error);
      stepTimers.forEach(clearTimeout);
      setSyncStep((prev) => { const next = { ...prev }; delete next[connectionId]; return next; });
      setSyncBanners((prev) => ({
        ...prev,
        [connectionId]: {
          type: "error",
          imported: 0,
          skipped: 0,
          duplicates: 0,
          error: error instanceof Error ? error.message : t("creditCardSync.syncFailed"),
        },
      }));
    } finally {
      setIsSyncing(null);
    }
  };

  const handleNoHistoryImport = async () => {
    setNoHistoryDialogOpen(false);
    if (noHistoryConnectionId) {
      await handleSync(noHistoryConnectionId, 30);
    }
  };

  const collectDuplicates = (
    results: CreditCardSyncResult[],
    fallbackConnectionId?: string,
  ): (PotentialDuplicate & { connectionId: string })[] => {
    const all: (PotentialDuplicate & { connectionId: string })[] = [];
    for (const r of results) {
      if (r.potentialDuplicates?.length) {
        for (const d of r.potentialDuplicates) {
          all.push({ ...d, connectionId: r.connectionId || fallbackConnectionId || "" });
        }
      }
    }
    return all;
  };

  const handleResolveDuplicates = async () => {
    setIsResolvingDuplicates(true);
    try {
      const resolutions: DuplicateResolution[] = pendingDuplicates.map((d) => ({
        existingTransactionId: d.existingTransactionId,
        action: duplicateActions[d.existingTransactionId] || "replace",
        ccExternalId: d.ccExternalId,
        ccDescription: d.ccDescription,
        ccAmount: d.ccAmount,
        ccDate: d.ccDate,
        ccCardNumber: d.ccCardNumber,
        ccPaymentType: d.ccPaymentType,
        ccMerchantName: d.ccMerchantName,
        connectionId: d.connectionId,
      }));
      const result = await creditCardSyncApi.resolveDuplicates(resolutions);
      toast.success(
        `${result.replaced} ${t("creditCardSync.duplicatesReplaced")}, ${result.kept} ${t("creditCardSync.duplicatesKept")}`,
      );
      setDuplicateReviewOpen(false);
      setPendingDuplicates([]);
      await loadData();
    } catch (error) {
      console.error("Resolve duplicates failed:", error);
      toast.error(t("creditCardSync.resolveFailed"));
    } finally {
      setIsResolvingDuplicates(false);
    }
  };

  const collectRecurringOverlaps = (results: CreditCardSyncResult[]): RecurringOverlap[] => {
    const all: RecurringOverlap[] = [];
    for (const r of results) {
      if (r.recurringOverlaps?.length) {
        all.push(...r.recurringOverlaps);
      }
    }
    // Deduplicate by recurringPaymentId
    const seen = new Set<string>();
    return all.filter((o) => {
      if (seen.has(o.recurringPaymentId)) return false;
      seen.add(o.recurringPaymentId);
      return true;
    });
  };

  const showOverlapsIfAny = (results: CreditCardSyncResult[]) => {
    const overlaps = collectRecurringOverlaps(results);
    if (overlaps.length > 0) {
      setPendingOverlaps(overlaps);
      setSelectedOverlaps(new Set(overlaps.map((o) => o.recurringPaymentId)));
      setRecurringOverlapOpen(true);
    }
  };

  const handleDeactivateRecurring = async () => {
    setIsDeactivatingRecurring(true);
    try {
      const ids = Array.from(selectedOverlaps);
      const result = await creditCardSyncApi.deactivateRecurring(ids);
      toast.success(
        `${result.deactivated} ${t("creditCardSync.recurringArchived")}`,
      );
      setRecurringOverlapOpen(false);
      setPendingOverlaps([]);
      await loadData();
    } catch (error) {
      console.error("Deactivate recurring failed:", error);
      toast.error(t("creditCardSync.archiveFailed"));
    } finally {
      setIsDeactivatingRecurring(false);
    }
  };

  const handleSyncAll = async () => {
    setIsSyncingAll(true);
    setSyncBanners({});

    for (const conn of connections) {
      if (conn.enabled) {
        setSyncStep((prev) => ({ ...prev, [conn.id]: 0 }));
      }
    }

    try {
      const results = await creditCardSyncApi.sync();

      setSyncStep({});

      const allDuplicates = collectDuplicates(results);
      if (allDuplicates.length > 0) {
        setPendingDuplicates(allDuplicates);
        const defaultActions: Record<string, 'replace' | 'keep_both'> = {};
        allDuplicates.forEach((d) => {
          defaultActions[d.existingTransactionId] = 'replace';
        });
        setDuplicateActions(defaultActions);
        setDuplicateReviewOpen(true);
      }

      const banners: Record<string, SyncBanner> = {};
      for (const r of results) {
        const connId = r.connectionId || "";
        const dups = (r.potentialDuplicates || []).length;
        banners[connId] = r.success
          ? { type: "success", imported: r.transactionsImported, skipped: r.transactionsSkipped, duplicates: dups }
          : { type: "error", imported: 0, skipped: 0, duplicates: 0, error: r.error || "Unknown error" };
      }
      setSyncBanners(banners);

      showOverlapsIfAny(results);
      await loadData();
    } catch (error) {
      console.error("Sync all failed:", error);
      setSyncStep({});
      toast.error(t("creditCardSync.syncFailed"));
    } finally {
      setIsSyncingAll(false);
    }
  };

  const getStatusBadge = (connection: CreditCardConnectionDto) => {
    if (connection.lastSyncStatus === "success") {
      return (
        <Badge variant="outline" className="text-green-600 border-green-600 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {t("creditCardSync.syncSuccess")}
        </Badge>
      );
    }
    if (connection.lastSyncStatus === "failed") {
      return (
        <Badge variant="outline" className="text-red-600 border-red-600 gap-1">
          <XCircle className="h-3 w-3" />
          {t("creditCardSync.syncFailed")}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-muted-foreground gap-1">
        <Clock className="h-3 w-3" />
        {t("creditCardSync.never")}
      </Badge>
    );
  };

  const providerLabel = (p: CreditCardProvider) =>
    t(`creditCardSync.providers.${p}`);

  const toggleCard = (accountNumber: string) => {
    setSelectedCards((prev) => {
      const next = new Set(prev);
      if (next.has(accountNumber)) next.delete(accountNumber);
      else next.add(accountNumber);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (discoveredCards.every((c) => selectedCards.has(c.accountNumber))) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(discoveredCards.map((c) => c.accountNumber)));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className={isRTL ? "text-right" : "text-left"}>
          <h1 className="text-3xl font-bold">{t("creditCardSync.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("creditCardSync.description")}</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse-dot {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shimmer {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Header */}
      <div className={`flex items-center justify-between ${isRTL ? "flex-row-reverse" : ""}`}>
        <div className={isRTL ? "text-right" : "text-left"}>
          <h1 className="text-3xl font-bold">{t("creditCardSync.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("creditCardSync.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          {connections.length > 0 && (
            <Button
              variant="outline"
              onClick={handleSyncAll}
              disabled={isSyncingAll || !!isSyncing}
            >
              {isSyncingAll ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {t("creditCardSync.syncAll")}
            </Button>
          )}
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("creditCardSync.addConnection")}
          </Button>
        </div>
      </div>

      {/* Schedule info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>{t("creditCardSync.schedule")}</AlertDescription>
      </Alert>

      {/* Connections list */}
      {connections.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CreditCard className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold">{t("creditCardSync.noConnections")}</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-md text-center">
              {t("creditCardSync.noConnectionsDesc")}
            </p>
            <Button className="mt-6" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t("creditCardSync.addConnection")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {connections.map((connection) => {
            const banner = syncBanners[connection.id];
            const currentSyncStep = syncStep[connection.id];
            const isSyncingThis = isSyncing === connection.id || currentSyncStep !== undefined;

            const syncStepLabels = [
              t("creditCardSync.syncStepConnect", { provider: providerLabel(connection.provider) }),
              t("creditCardSync.syncStepLogin"),
              t("creditCardSync.syncStepFetch"),
              t("creditCardSync.syncStepProcess"),
            ];

            return (
              <Card key={connection.id} className="overflow-hidden">
                {/* Sync progress bar */}
                {isSyncingThis && (
                  <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-primary truncate">
                          {syncStepLabels[currentSyncStep ?? 0]}
                          <PulsingDots />
                        </p>
                        <div className="flex gap-1 mt-2">
                          {syncStepLabels.map((_, i) => (
                            <div
                              key={i}
                              className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                                i <= (currentSyncStep ?? 0)
                                  ? "bg-primary"
                                  : "bg-primary/20"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sync result banner */}
                {banner && !isSyncingThis && (
                  <div
                    className={`border-b px-4 py-3 animate-[slideUp_0.3s_ease-out] ${
                      banner.type === "success"
                        ? "bg-gradient-to-r from-green-50 to-emerald-50/50 dark:from-green-950/30 dark:to-emerald-950/20"
                        : "bg-gradient-to-r from-red-50 to-rose-50/50 dark:from-red-950/30 dark:to-rose-950/20"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {banner.type === "success" ? (
                          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center shrink-0">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        )}
                        <div>
                          {banner.type === "success" ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-green-700 dark:text-green-400">
                                {t("creditCardSync.syncComplete")}
                              </span>
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 border-0">
                                {banner.imported} {t("creditCardSync.imported")}
                              </Badge>
                              {banner.skipped > 0 && (
                                <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground border-0">
                                  {banner.skipped} {t("creditCardSync.skipped")}
                                </Badge>
                              )}
                              {banner.duplicates > 0 && (
                                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 border-0">
                                  {banner.duplicates} {t("creditCardSync.duplicatesFound")}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm font-medium text-red-700 dark:text-red-400">
                              {t("creditCardSync.syncFailed")}{banner.error ? `: ${banner.error}` : ""}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 shrink-0"
                        onClick={() => setSyncBanners((prev) => { const next = { ...prev }; delete next[connection.id]; return next; })}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}

                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <CreditCardProviderLogo provider={connection.provider} size="lg" />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{connection.displayName}</h3>
                          <Badge variant="secondary">{providerLabel(connection.provider)}</Badge>
                          {connection.selectedCards && connection.selectedCards.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {connection.selectedCards.length} card(s)
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          {getStatusBadge(connection)}
                          {connection.lastSyncAt && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {t("creditCardSync.lastSync")}:{" "}
                              {format(new Date(connection.lastSyncAt), "dd/MM/yyyy HH:mm")}
                              {connection.lastSyncTransactionCount > 0 && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                  {connection.lastSyncTransactionCount} {t("creditCardSync.imported")}
                                </Badge>
                              )}
                            </span>
                          )}
                          <span>{connection.syncCount} {t("creditCardSync.syncsCount")}</span>
                        </div>
                        {connection.lastSyncError && connection.lastSyncStatus === "failed" && !banner && (
                          <p className="text-xs text-destructive mt-1">{connection.lastSyncError}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={connection.enabled}
                        onCheckedChange={(checked) => handleToggle(connection.id, checked)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(connection)}
                        title={t("creditCardSync.editConnection")}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync(connection.id)}
                        disabled={!connection.enabled || isSyncingThis || isSyncingAll}
                        title={t("creditCardSync.syncNow")}
                      >
                        {isSyncingThis ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setConnectionToDelete(connection.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Connection Dialog */}
      <Dialog
        open={addDialogOpen}
        onOpenChange={(open) => {
          if (!open && addStep !== "connecting" && addStep !== "archiveRecurring" && addStep !== "initialImport") {
            setAddDialogOpen(false);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-md">
          {addStep === "form" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t("creditCardSync.addConnection")}
                </DialogTitle>
                <DialogDescription>{t("creditCardSync.description")}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("creditCardSync.provider")}</Label>
                  <Select
                    value={provider}
                    onValueChange={(v) => setProvider(v as CreditCardProvider)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDERS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {t(p.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t("creditCardSync.displayName")}</Label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={t("creditCardSync.displayNamePlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("creditCardSync.defaultCategory")}</Label>
                  <Select value={defaultCategoryId} onValueChange={setDefaultCategoryId}>
                    <SelectTrigger>
                      <SelectValue placeholder="---" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories
                        .filter((c) => c.type === "expense")
                        .map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: cat.color }}
                              />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t("creditCardSync.defaultCategoryDesc")}
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    {t("creditCardSync.credentials")}
                  </Label>
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      {t("creditCardSync.credentialsSecurity")}
                    </AlertDescription>
                  </Alert>

                  {provider === "isracard" ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="israeliId">{t("creditCardSync.israeliId")}</Label>
                        <Input
                          id="israeliId"
                          value={israeliId}
                          onChange={(e) => setIsraeliId(e.target.value)}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="card6">{t("creditCardSync.card6Digits")}</Label>
                        <Input
                          id="card6"
                          value={card6Digits}
                          onChange={(e) => setCard6Digits(e.target.value)}
                          maxLength={6}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">{t("creditCardSync.password")}</Label>
                        <Input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                          dir="ltr"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="username">{t("creditCardSync.username")}</Label>
                        <Input
                          id="username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          autoComplete="off"
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">{t("creditCardSync.password")}</Label>
                        <Input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                          dir="ltr"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleTestConnection}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {t("creditCardSync.save")}
                </Button>
              </DialogFooter>
            </>
          )}

          {addStep === "connecting" && (
            <ConnectingAnimation
              provider={providerLabel(provider)}
              step={connectAnimStep}
            />
          )}

          {addStep === "selectCards" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t("creditCardSync.selectCards")}
                </DialogTitle>
              </DialogHeader>

              <CardSelector
                cards={discoveredCards}
                selectedCards={selectedCards}
                onToggle={toggleCard}
                onSelectAll={toggleSelectAll}
              />

              <DialogFooter>
                <Button variant="outline" onClick={() => setAddStep("form")}>
                  Back
                </Button>
                <Button onClick={handleConfirmCards} disabled={selectedCards.size === 0}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t("creditCardSync.continueWithSelected", {
                    count: selectedCards.size,
                  })}
                </Button>
              </DialogFooter>
            </>
          )}

          {addStep === "initialImport" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  {t("creditCardSync.initialImportTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("creditCardSync.initialImportDesc")}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 py-2">
                <div
                  onClick={() => {
                    const now = new Date();
                    const dayOfMonth = now.getDate();
                    proceedAfterImportChoice(dayOfMonth);
                  }}
                  className="flex items-start gap-4 p-4 rounded-xl border-2 border-primary bg-primary/5 cursor-pointer hover:shadow-sm transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t("creditCardSync.initialImportThisMonth")}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("creditCardSync.initialImportThisMonthDesc")}
                    </p>
                  </div>
                </div>

                <div
                  onClick={() => proceedAfterImportChoice(undefined)}
                  className="flex items-start gap-4 p-4 rounded-xl border-2 border-border cursor-pointer hover:border-primary/30 hover:bg-muted/50 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                    <SkipForward className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t("creditCardSync.initialImportSkip")}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("creditCardSync.initialImportSkipDesc")}
                    </p>
                  </div>
                </div>
              </div>

              <Alert className="border-blue-200 bg-blue-50/80 dark:bg-blue-950/30">
                <Upload className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
                  {t("creditCardSync.initialImportHint")}
                </AlertDescription>
              </Alert>

              <DialogFooter>
                <Button variant="outline" onClick={() => setAddStep("selectCards")}>
                  {t("creditCardSync.back")}
                </Button>
              </DialogFooter>
            </>
          )}

          {addStep === "archiveRecurring" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  {t("creditCardSync.recurringOverlapTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("creditCardSync.recurringOverlapDesc")}
                </DialogDescription>
              </DialogHeader>

              <Alert className="mt-2 border-amber-200 bg-amber-50/80 dark:bg-amber-950/30">
                <Info className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
                  {t("creditCardSync.recurringOverlapRecommended")}
                </AlertDescription>
              </Alert>

              <div className="space-y-2 mt-3 max-h-[40vh] overflow-y-auto">
                {addFlowRecurring.map((rp) => (
                  <Card
                    key={rp.id}
                    className={`border cursor-pointer transition-all ${
                      addFlowSelectedRecurring.has(rp.id)
                        ? 'border-amber-400 bg-amber-50/60 dark:bg-amber-950/20 shadow-sm'
                        : 'border-muted hover:border-muted-foreground/30'
                    }`}
                    onClick={() => {
                      setAddFlowSelectedRecurring((prev) => {
                        const next = new Set(prev);
                        if (next.has(rp.id)) next.delete(rp.id);
                        else next.add(rp.id);
                        return next;
                      });
                    }}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <Checkbox
                        checked={addFlowSelectedRecurring.has(rp.id)}
                        onCheckedChange={(checked) => {
                          setAddFlowSelectedRecurring((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(rp.id);
                            else next.delete(rp.id);
                            return next;
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{rp.description}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-muted-foreground" dir="ltr">
                            ₪{Math.abs(Number(rp.amount)).toFixed(2)}
                          </span>
                          {rp.paymentType && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              {rp.paymentType}
                            </Badge>
                          )}
                          {rp.dayOfMonth && (
                            <span className="text-[10px] text-muted-foreground">
                              {t("creditCardSync.dayOfMonth", { day: rp.dayOfMonth })}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <DialogFooter className="flex flex-col gap-2 sm:flex-row mt-4">
                <Button variant="ghost" size="sm" onClick={handleAddFlowArchiveSkip}>
                  {t("creditCardSync.keepRecurring")}
                </Button>
                <Button
                  onClick={handleAddFlowArchiveConfirm}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {t("creditCardSync.archiveSelected")} ({addFlowSelectedRecurring.size}/{addFlowRecurring.length})
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("creditCardSync.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("creditCardSync.deleteConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* No History — offer to import last 30 days */}
      <AlertDialog open={noHistoryDialogOpen} onOpenChange={setNoHistoryDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              {t("creditCardSync.noHistoryTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("creditCardSync.noHistoryDesc", { provider: noHistoryProvider })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setNoHistoryDialogOpen(false); setNoHistoryConnectionId(null); }}>
              {t("creditCardSync.noHistorySkip")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleNoHistoryImport}>
              <Download className="h-4 w-4 mr-2" />
              {t("creditCardSync.noHistoryImport")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Connection Dialog */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (!open && editStep !== "connecting") {
            setEditDialogOpen(false);
            setEditConnection(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          {editStep === "form" && editConnection && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pencil className="h-5 w-5" />
                  {t("creditCardSync.editConnection")}
                </DialogTitle>
                <DialogDescription>
                  {t("creditCardSync.editConnectionDesc")}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("creditCardSync.provider")}</Label>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/50">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{providerLabel(editConnection.provider)}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("creditCardSync.displayName")}</Label>
                  <Input
                    value={editDisplayName}
                    onChange={(e) => setEditDisplayName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("creditCardSync.defaultCategory")}</Label>
                  <Select value={editDefaultCategoryId} onValueChange={setEditDefaultCategoryId}>
                    <SelectTrigger>
                      <SelectValue placeholder="---" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories
                        .filter((c) => c.type === "expense")
                        .map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: cat.color }}
                              />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {editConnection.selectedCards && editConnection.selectedCards.length > 0 && (
                  <div className="space-y-2">
                    <Label>{t("creditCardSync.syncedCards")}</Label>
                    <div className="flex flex-wrap gap-2">
                      {editConnection.selectedCards.map((card) => (
                        <Badge key={card} variant="outline" className="gap-1">
                          <CreditCard className="h-3 w-3" />
                          ****{card.slice(-4)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                <div className="space-y-3">
                  {!showEditCredentials ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setShowEditCredentials(true)}
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      {t("creditCardSync.updateCredentials")}
                    </Button>
                  ) : (
                    <>
                      <Label className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        {t("creditCardSync.updateCredentials")}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t("creditCardSync.updateCredentialsDesc")}
                      </p>

                      {editConnection.provider === "isracard" ? (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="editIsraeliId">{t("creditCardSync.israeliId")}</Label>
                            <Input
                              id="editIsraeliId"
                              value={editIsraeliId}
                              onChange={(e) => setEditIsraeliId(e.target.value)}
                              placeholder={t("creditCardSync.leaveBlankKeep")}
                              autoComplete="off"
                              dir="ltr"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="editCard6">{t("creditCardSync.card6Digits")}</Label>
                            <Input
                              id="editCard6"
                              value={editCard6Digits}
                              onChange={(e) => setEditCard6Digits(e.target.value)}
                              placeholder={t("creditCardSync.leaveBlankKeep")}
                              maxLength={6}
                              autoComplete="off"
                              dir="ltr"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="editPassword">{t("creditCardSync.password")}</Label>
                            <Input
                              id="editPassword"
                              type="password"
                              value={editPassword}
                              onChange={(e) => setEditPassword(e.target.value)}
                              placeholder={t("creditCardSync.leaveBlankKeep")}
                              autoComplete="new-password"
                              dir="ltr"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="editUsername">{t("creditCardSync.username")}</Label>
                            <Input
                              id="editUsername"
                              value={editUsername}
                              onChange={(e) => setEditUsername(e.target.value)}
                              placeholder={t("creditCardSync.leaveBlankKeep")}
                              autoComplete="off"
                              dir="ltr"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="editPassword">{t("creditCardSync.password")}</Label>
                            <Input
                              id="editPassword"
                              type="password"
                              value={editPassword}
                              onChange={(e) => setEditPassword(e.target.value)}
                              placeholder={t("creditCardSync.leaveBlankKeep")}
                              autoComplete="new-password"
                              dir="ltr"
                            />
                          </div>
                        </>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={handleEditTestConnection}
                        disabled={editStep === "connecting"}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {t("creditCardSync.testAndRediscover")}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  {t("creditCardSync.cancel")}
                </Button>
                <Button onClick={handleEditSave} disabled={isSavingEdit}>
                  {isSavingEdit ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {t("creditCardSync.saveChanges")}
                </Button>
              </DialogFooter>
            </>
          )}

          {editStep === "connecting" && editConnection && (
            <ConnectingAnimation
              provider={providerLabel(editConnection.provider)}
              step={editAnimStep}
            />
          )}

          {editStep === "selectCards" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t("creditCardSync.selectCards")}
                </DialogTitle>
              </DialogHeader>

              <CardSelector
                cards={editDiscoveredCards}
                selectedCards={editSelectedCards}
                onToggle={editToggleCard}
                onSelectAll={editToggleSelectAll}
              />

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditStep("form")}>
                  {t("creditCardSync.back")}
                </Button>
                <Button
                  onClick={() => setEditStep("form")}
                  disabled={editSelectedCards.size === 0}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t("creditCardSync.confirmCards")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Review Dialog */}
      <Dialog open={duplicateReviewOpen} onOpenChange={(open) => { if (!open && !isResolvingDuplicates) setDuplicateReviewOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t("creditCardSync.duplicateReviewTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("creditCardSync.duplicateReviewDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {pendingDuplicates.map((dup) => {
              const action = duplicateActions[dup.existingTransactionId] || 'replace';
              const sourceMap: Record<string, string> = {
                web: t("transactions.sourceWeb"),
                whatsapp: t("transactions.sourceWhatsapp"),
                automation: t("transactions.sourceAutomation"),
                recurring: t("transactions.sourceRecurring"),
                import: t("transactions.sourceImport"),
              };
              const sourceLabel = sourceMap[dup.existingSource || 'web'] || dup.existingSource || t("transactions.sourceWeb");

              return (
                <Card key={dup.existingTransactionId} className={`border ${action === 'replace' ? 'border-amber-200 bg-amber-50/50 dark:bg-amber-950/20' : 'border-muted'}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex flex-col sm:flex-row items-start gap-3">
                      {/* Existing transaction */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          {t("creditCardSync.existingTransaction")}
                          <Badge variant="outline" className="ml-2 text-[10px]">{sourceLabel}</Badge>
                        </p>
                        <p className="text-sm font-medium truncate">{dup.existingDescription}</p>
                        <p className="text-xs text-muted-foreground">
                          ₪{Math.abs(dup.existingAmount).toFixed(2)} · {dup.existingDate}
                        </p>
                      </div>

                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block mt-4" />

                      {/* CC transaction */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          {t("creditCardSync.ccTransaction")}
                          {dup.ccCardNumber && (
                            <span className="ml-1 text-[10px] text-muted-foreground">•••• {dup.ccCardNumber}</span>
                          )}
                        </p>
                        <p className="text-sm font-medium truncate">{dup.ccDescription}</p>
                        <p className="text-xs text-muted-foreground">
                          ₪{Math.abs(dup.ccAmount).toFixed(2)} · {dup.ccDate}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-1">
                      <Button
                        variant={action === 'replace' ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => setDuplicateActions((prev) => ({ ...prev, [dup.existingTransactionId]: 'replace' }))}
                      >
                        <Replace className="h-3 w-3 mr-1" />
                        {t("creditCardSync.replaceWithCC")}
                      </Button>
                      <Button
                        variant={action === 'keep_both' ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => setDuplicateActions((prev) => ({ ...prev, [dup.existingTransactionId]: 'keep_both' }))}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        {t("creditCardSync.keepBoth")}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row mt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mr-auto">
              <Badge variant="outline">
                {Object.values(duplicateActions).filter((a) => a === 'replace').length} {t("creditCardSync.willReplace")}
              </Badge>
              <Badge variant="outline">
                {Object.values(duplicateActions).filter((a) => a === 'keep_both').length} {t("creditCardSync.willKeepBoth")}
              </Badge>
            </div>
            <Button
              variant="outline"
              onClick={() => { setDuplicateReviewOpen(false); setPendingDuplicates([]); }}
              disabled={isResolvingDuplicates}
            >
              {t("creditCardSync.skipDuplicates")}
            </Button>
            <Button
              onClick={handleResolveDuplicates}
              disabled={isResolvingDuplicates}
            >
              {isResolvingDuplicates ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("creditCardSync.resolving")}
                </>
              ) : (
                t("creditCardSync.applyResolutions")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Overlap Dialog */}
      <Dialog open={recurringOverlapOpen} onOpenChange={(open) => { if (!open && !isDeactivatingRecurring) setRecurringOverlapOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t("creditCardSync.recurringOverlapTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("creditCardSync.recurringOverlapDesc")}
            </DialogDescription>
          </DialogHeader>

          <Alert className="mt-3 border-amber-200 bg-amber-50/80 dark:bg-amber-950/30">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
              {t("creditCardSync.recurringOverlapRecommended")}
            </AlertDescription>
          </Alert>

          <div className="space-y-2 mt-3">
            {pendingOverlaps.map((o) => (
              <Card
                key={o.recurringPaymentId}
                className={`border cursor-pointer transition-all ${
                  selectedOverlaps.has(o.recurringPaymentId)
                    ? 'border-amber-400 bg-amber-50/60 dark:bg-amber-950/20 shadow-sm'
                    : 'border-muted hover:border-muted-foreground/30'
                }`}
                onClick={() => {
                  setSelectedOverlaps((prev) => {
                    const next = new Set(prev);
                    if (next.has(o.recurringPaymentId)) next.delete(o.recurringPaymentId);
                    else next.add(o.recurringPaymentId);
                    return next;
                  });
                }}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <Checkbox
                    checked={selectedOverlaps.has(o.recurringPaymentId)}
                    onCheckedChange={(checked) => {
                      setSelectedOverlaps((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(o.recurringPaymentId);
                        else next.delete(o.recurringPaymentId);
                        return next;
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{o.recurringDescription}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground" dir="ltr">
                        ₪{o.recurringAmount.toFixed(2)}
                      </span>
                      {o.categoryName && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {o.categoryName}
                        </Badge>
                      )}
                      {o.paymentType && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {o.paymentType}
                        </Badge>
                      )}
                      {o.dayOfMonth && (
                        <span className="text-[10px] text-muted-foreground">
                          {t("creditCardSync.dayOfMonth", { day: o.dayOfMonth })}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setRecurringOverlapOpen(false); setPendingOverlaps([]); }}
              disabled={isDeactivatingRecurring}
            >
              {t("creditCardSync.keepRecurring")}
            </Button>
            <Button
              onClick={handleDeactivateRecurring}
              disabled={isDeactivatingRecurring || selectedOverlaps.size === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isDeactivatingRecurring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("creditCardSync.archiving")}
                </>
              ) : (
                <>
                  {t("creditCardSync.archiveSelected")} ({selectedOverlaps.size}/{pendingOverlaps.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
